import React, { useCallback, useEffect, useRef, useState } from 'react';

import { NavLink } from 'react-router-dom';
import { useHistory } from 'react-router';
import styled from 'styled-components/macro';
import { faSearch, faTimes } from '@fortawesome/pro-light-svg-icons';
import cx from 'classnames';
import { SearchBoxAutocomplete } from './SearchBoxAutocomplete';
import { useSearchAutoComplete } from './useSearchAutoComplete';
import { ToggleAnimated } from 'src/client/containers/ToggleAnimated/ToggleAnimated';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { buildSearchPath } from 'src/shared/sanitize';

type Props = {
  isOpened: boolean;
  onClose: () => void;
  searchToggleRef: React.RefObject<HTMLDivElement>;
};

export const SearchBoxComponent = (props: Props) => {
  const { isOpened, onClose, searchToggleRef } = props;

  const searchInput = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState<number | undefined>();
  const searchBox = useRef<HTMLDivElement>(null);
  const history = useHistory();

  const autoCompleteValues = useSearchAutoComplete(searchTerm, {
    onSuccess: () => setSelectedOption(undefined),
  });

  const handleClose = useCallback(() => {
    onClose();
    setSearchTerm('');
    setSelectedOption(undefined);
  }, [onClose]);

  useEffect(() => {
    if (!isOpened) {
      return;
    }

    const handleMouseClick = (e: MouseEvent) => {
      const target = e.target;
      if (
        !(target instanceof Element) ||
        searchBox.current?.contains(target) ||
        searchToggleRef.current?.contains(target) ||
        autocompleteRef.current?.contains(target)
      ) {
        return;
      }

      handleClose();
    };

    document.addEventListener('mousedown', handleMouseClick);
    return () => {
      document.removeEventListener('mousedown', handleMouseClick);
    };
  }, [handleClose, isOpened, searchToggleRef]);

  const keyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      const { key } = e;

      if (key === 'Enter') {
        if (selectedOption === undefined) {
          return;
        }

        const selected = autoCompleteValues[selectedOption];
        if (!selected) {
          return;
        }

        e.preventDefault();
        history.push(buildSearchPath([selected]));
        handleClose();
        return;
      }

      if ((key !== 'ArrowUp' && key !== 'ArrowDown') || !autoCompleteValues.length) {
        return;
      }

      e.preventDefault();
      const delta = key === 'ArrowDown' ? 1 : -1;

      setSelectedOption((prev) => {
        const next = prev === undefined ? (delta > 0 ? 0 : autoCompleteValues.length - 1) : prev + delta;
        return next >= 0 && next < autoCompleteValues.length ? next : undefined;
      });
    },
    [autoCompleteValues, handleClose, selectedOption, history],
  );

  const submitForm = (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (!searchTerm.trim()) {
      return;
    }

    history.push(buildSearchPath([searchTerm]));
    handleClose();
  };

  const handleSearchTermChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setSelectedOption(undefined);
  };

  const handleSearchEntered = () => {
    searchInput.current?.focus({ preventScroll: true });
  };

  return (
    <SearchWrapper ref={searchBox} className={cx({ 'has-autocomplete': autoCompleteValues.length > 0 })}>
      <ToggleAnimated
        animationName="searchbox"
        animationDuration={250}
        isExpanded={isOpened}
        mountOnEnter
        onEntered={handleSearchEntered}
        unmountOnExit
      >
        <InnerContainer className="container">
          <SearchForm onSubmit={submitForm} className="form">
            <SearchInputGroup className={cx('input-group', { 'autocomplete-borders': autoCompleteValues.length > 0 })}>
              <SearchButton className="input-group-addon" onClick={submitForm}>
                <FontAwesomeIcon icon={faSearch} />
              </SearchButton>
              <SearchInput
                name="search"
                ref={searchInput}
                type="text"
                autoComplete="off"
                placeholder="Search galleries and models"
                className="form-control input-lg"
                onKeyDown={keyDown}
                onChange={handleSearchTermChange}
                value={searchTerm}
              />
              <CloseButton className="input-group-addon" onClick={handleClose}>
                <FontAwesomeIcon icon={faTimes} size="2x" />
              </CloseButton>
            </SearchInputGroup>
            <SearchBoxAutocomplete
              autocompleteRef={autocompleteRef}
              autoCompleteValues={autoCompleteValues}
              selectedOption={selectedOption}
              handleItemClick={handleClose}
              keyDown={keyDown}
              setSelectedOption={setSelectedOption}
              searchTerm={searchTerm}
            />
          </SearchForm>
          <AdvancedSearchLink to="/search" onClick={handleClose}>
            Advanced Search
          </AdvancedSearchLink>
        </InnerContainer>
      </ToggleAnimated>
    </SearchWrapper>
  );
};

const SearchWrapper = styled.div`
  position: relative;
  z-index: 1000;

  &.has-autocomplete .searchbox-enter-done {
    overflow: visible;
  }
`;

const InnerContainer = styled.div`
  padding: 10px 0 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
`;

const SearchForm = styled.form`
  display: block;
  margin-top: 0;
  margin-bottom: 10px;
  position: relative;
`;

const SearchInput = styled.input`
  flex: 1;
  font-weight: 300;
  font-size: 1.6rem;
  box-shadow: none;
  height: 46px;
  padding: 10px;
  line-height: 1.33;
  max-width: none;
  width: 100%;
  display: block;

  /* Blame Bootstrap */
  &&& {
    color: #555;
    border: 0;
    background: #fff;
  }

  :focus {
    border: 0;
    box-shadow: none;
  }
`;

const SearchInputGroup = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  border-radius: 5px;
  background: #fff;
  border: 1px solid ${(props) => props.theme.primary3};

  &.autocomplete-borders {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }

  a {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 0;
    box-shadow: none;
    padding: 0;
    height: 46px;
    width: 46px;
  }
`;

const SearchButton = styled.a`
  font-size: 1.3rem;
  cursor: pointer;
  transition: 0.3s opacity;

  svg {
    color: #adadad;
  }

  :hover,
  :focus {
    opacity: 1;
  }
`;

const CloseButton = styled.a`
  svg {
    color: #a94442;
    cursor: pointer;
    transition: 0.3s opacity;
  }

  :hover,
  :focus {
    border: 0;
    text-decoration: none;
    outline: none;
  }
`;

const AdvancedSearchLink = styled(NavLink)`
  align-self: flex-end;
`;
